import { Injectable } from '@angular/core';
import { FirebaseAnalytics, LogEventOptions } from '@capacitor-firebase/analytics';
import { AppFeaturesService } from '../portal/app-features.service';
import { ActivationEnd, Router } from '@angular/router';
import { catchError, combineLatestWith, debounceTime, delay, filter, from, map, of, switchMap, tap } from 'rxjs';
import { FmErrorHandlerService } from './fm-error-handler.service';
import { MetaAppEventsService } from './meta-app-events.service';
import { Preferences } from '@capacitor/preferences';
import { Store } from '@ngxs/store';
import { AppFeaturesState } from '../portal/app-feature.state';

interface GtagFunctionParams {
  resource_id?: string;
  [key: string]: string | boolean | undefined;
}

type GtagFunction = (
  command: 'event' | 'config' | 'set',
  targetIdOrEventName: AllrotorsAnalyticEvents | AnalyticsTarget,
  params?: GtagFunctionParams
) => void;

declare let gtag: GtagFunction;

export enum LogEventType {
  APP_OPEN = 'app_open',
  PAGE_VIEW = 'page_view',
  SCROLL = 'scroll',
  CLICK = 'click',
}

export enum AllrotorsAnalyticEvents {
  APP_OPEN = 'allrotors_app_open',
  USER_REGISTER = 'allrotors_user_register',
  USER_LOGIN = 'allrotors_user_login',
  LOCATION_CREATE = 'allrotors_location_create',
  LOCATION_UPDATE = 'allrotors_location_update',
  LOCATION_DELETE = 'allrotors_location_delete',
  VEHICLE_CREATE = 'allrotors_vehicle_create',
  VEHICLE_UPDATE = 'allrotors_vehicle_update',
  VEHICLE_DELETE = 'allrotors_vehicle_delete',
  SCHEDULE_ORDER = 'allrotors_schedule_new_order',
  COMPLETE_ORDER = 'allrotors_complete_order',
  CANCEL_ORDER = 'allrotors_cancel_order',
  START_ORDER = 'allrotors_start_order',
}

enum AnalyticsTarget {
  USER_PROPERTIES = 'user_properties',
}

export enum UTMVariable {
  utm_source = 'utm_source',
  utm_medium = 'utm_medium',
  utm_campaign = 'utm_campaign',
  utm_content = 'utm_content',
  utm_term = 'utm_term',
}

@Injectable({
  providedIn: 'root',
})
export class GoogleAnalyticsService {
  #adVariables: { [key: string]: string } = {};

  constructor(
    private appFeaturesService: AppFeaturesService,
    private router: Router,
    private FmErrorHandlerService: FmErrorHandlerService,
    private metaAppEventsService: MetaAppEventsService,
    private store: Store
  ) {
    from(this.setEnabled(true))
      .pipe(
        combineLatestWith([this.store.select(AppFeaturesState.googleAnalytics).pipe(filter((enabled) => enabled))]),
        switchMap(() => from(this.captureUTMVariables())),
        debounceTime(200),
        switchMap(() => from(this.setTimeoutDuration())),
        switchMap(() => from(this.setUserProperties())),
        switchMap(() => from(this.logEvent({ name: LogEventType.APP_OPEN }))),
        switchMap(() => of(this.logCustomEvents(AllrotorsAnalyticEvents.APP_OPEN)))
      )
      .subscribe();

    if (!this.appFeaturesService.isNativeIos) {
      return;
    }
    this.setupRouteMonitor();
    this.setUpClickEventMonitor();
  }

  async captureUTMVariables(eventUrl?: string) {
    try {
      const url = new URL(eventUrl || window.location.href);
      const utmKeys = Object.keys(UTMVariable);
      for await (const key of utmKeys) {
        const storedValue = await Preferences.get({ key });
        if (storedValue.value) {
          this.#adVariables[key] = storedValue.value;
        }

        const value = url.searchParams.get(key);
        if (value) {
          this.#adVariables[key] = value;
          await Preferences.set({
            key,
            value,
          });
        }
      }
    } catch (err) {
      console.error('Error caught !!!');
      console.error(err);
    }
  }

  private setupRouteMonitor() {
    if (!this.appFeaturesService.isNativeIos) {
      return;
    }
    this.router.events
      .pipe(
        filter((event) => {
          if (event instanceof ActivationEnd && event.snapshot.data.title) {
            return true;
          }
          return false;
        }),
        map(async (event) => {
          if (!(event instanceof ActivationEnd)) {
            return '';
          }
          const title = event.snapshot.data.title;
          await this.setCurrentScreen(title);
          await this.logEvent({
            name: LogEventType.PAGE_VIEW,
            params: {
              page_location: window.location.href,
              page_path: window.location.pathname,
              page_title: title,
            },
          });
          this.metaAppEventsService
            .logContentView({
              contentType: title,
              contentId: window.location.pathname,
            })
            .subscribe();
          return title;
        })
      )
      .subscribe();
  }

  private setUpClickEventMonitor() {
    if (!this.appFeaturesService.isNativeIos) {
      return;
    }
    document.querySelector('body')?.addEventListener('click', async () => {
      await this.logEvent({ name: LogEventType.CLICK });
    });
  }

  async setUserId(userId: string) {
    if (!this.appFeaturesService.isNativeIos) {
      return;
    }
    await FirebaseAnalytics.setUserId({
      userId: userId,
    });
  }

  async setUserProperties() {
    if (!this.appFeaturesService.isNativeIos) {
      if (gtag) {
        gtag('set', AnalyticsTarget.USER_PROPERTIES, {
          ...this.#adVariables,
        });
      }
      return;
    }
    for await (const [key, value] of Object.entries(this.#adVariables)) {
      await FirebaseAnalytics.setUserProperty({
        key,
        value,
      });
    }
  }

  private async setCurrentScreen(screenName: string) {
    if (!this.appFeaturesService.isNativeIos) {
      return;
    }
    await FirebaseAnalytics.setCurrentScreen({
      screenName,
      screenClassOverride: 'CAPBridgeViewController',
    });
  }

  async logEvent(option: LogEventOptions) {
    if (!this.appFeaturesService.isNativeIos) {
      return;
    }
    await FirebaseAnalytics.logEvent(option);
  }

  private async setTimeoutDuration() {
    if (!this.appFeaturesService.isNativeIos) {
      return;
    }
    await FirebaseAnalytics.setSessionTimeoutDuration({
      duration: 120,
    });
  }

  private async setEnabled(enabled: boolean) {
    if (!this.appFeaturesService.isNativeIos) {
      return;
    }
    await FirebaseAnalytics.setEnabled({
      enabled,
    });
  }

  private async isEnabled() {
    if (!this.appFeaturesService.isNativeIos) {
      return;
    }
    const { enabled } = await FirebaseAnalytics.isEnabled();
    return enabled;
  }

  private async resetAnalyticsData() {
    if (!this.appFeaturesService.isNativeIos) {
      return;
    }
    await FirebaseAnalytics.resetAnalyticsData();
  }

  logCustomEvents(event: AllrotorsAnalyticEvents, params?: { resource_id: string; orders_id?: string }) {
    try {
      if (this.appFeaturesService.isNativeIos) {
        from(
          this.logEvent({
            name: event,
            params: {
              ...(params ?? {}),
              allrotors_resource_id: params?.resource_id,
              allrotors_orders_id: params?.orders_id,
              allrotors_page_location: window.location.href,
              allrotors_page_path: window.location.pathname,
            },
          })
        )
          .pipe(
            catchError((err) => {
              console.error(err);
              return of(null);
            })
          )
          .subscribe();
      } else {
        gtag('event', event, {
          ...(params ?? {}),
          page_location: window.location.href ?? undefined,
          page_path: window.location.pathname ?? undefined,
        });
      }
    } catch (err) {
      console.error(err);
      this.FmErrorHandlerService.recordError(err);
    }
  }
}
